import { Badge, Select } from '@hgc/ui';
import type { JSX } from 'react';

import { useAppStore } from '../runtime/AppStoreContext';
import { useLinkStatus, usePrimaryVehicle, useVehicleList } from '../runtime/hooks';

/**
 * 机体选择器。
 *
 * 一条链路上出现多个 `sysid:compid` 是常态（飞控是 comp 1，伴飞计算机、云台、相机各占一个号），
 * 所以"谁是我现在操作的对象"必须是一个显式可见、可改的状态，而不是"默认取第一个"。
 * 选择结果直接交给注册表的 `setPrimary()`：用户显式指定之后，注册表的自动策略
 * （飞控优先）会**彻底关闭**，直到选回"自动" —— 一架飞机在天上时焦点自己跑了，
 * 比不自动切换严重得多。
 */
export function VehicleSelector(): JSX.Element {
  const store = useAppStore();
  const status = useLinkStatus();
  const vehicles = useVehicleList();
  const primary = usePrimaryVehicle();

  const empty = vehicles.length === 0;
  // 断开之后摘要里的 online/armed 停在最后一帧，不该再当实时状态显示
  const live = status.phase === 'connected';

  return (
    <div className="flex min-w-0 items-center gap-2">
      <Select
        aria-label="机体"
        size="sm"
        className="w-56"
        value={primary?.id ?? ''}
        placeholder="自动（飞控优先）"
        disabled={empty}
        title={empty ? '链路上还没有发现任何机体：这一步不需要手动做，收到第一帧就会自动出现' : undefined}
        options={vehicles.map((vehicle) => ({ value: vehicle.id, label: vehicle.label }))}
        onChange={(value) => {
          store.registry?.setPrimary(value === '' ? null : value);
        }}
      />

      {primary === null || !live ? null : (
        <>
          <Badge size="sm" variant="outline" level={primary.online ? 'ok' : 'caution'}>
            {primary.online ? '在线' : '失联'}
          </Badge>
          <Badge size="sm" variant="outline" level={primary.armed ? 'warning' : 'neutral'}>
            {primary.armed ? '已解锁' : '未解锁'}
          </Badge>
        </>
      )}
    </div>
  );
}
